import React, {useEffect, useMemo, useState} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Pratica, UserRole, ScadenzaDto, FinancialStats } from '../types';
import {
    AlertTriangle, DollarSign, Folder, User as UserIcon,
    ArrowUpRight, ArrowDownRight, TrendingUp, Clock,
    Activity, Building2, Plus, Calendar as CalendarIcon,
    CheckCircle, Trash2, MapPin, CalendarCheck, Receipt, Users, Shield,
    CheckSquare, ShieldAlert, CalendarDays, XCircle, ChevronRight, Eye, EyeOff, Hash,
} from "lucide-react";
import {
    PraticaService,
    ClientService,
    SedeService,
    ScadenzaService,
    IncaricoService,
    ServizioFiduciarioService,
    AmministrazioneService,
    ApprovazioniService,
    RelazioneGiornalieraService,
    TestDataService,
    RifiutoRecenteDto,
    EscalationAlertService,
    EscalationAlertDto,
} from '../services/apiService';
import { Modal } from '../components/Modal';
import { useToast } from "../components/ToastContext";
import { hasOrariDifformi, getOrariDifformiMinuti, useSogliaOrariDifformi, isIncaricoConcluso, formatMinutiBreve, getStatoOperativoIncarico } from '../utils/incaricoUtils';

// StatCard
interface StatCardProps {
    label: string;
    valore: string | number;
    icon: React.ReactElement;
    colorClass: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'indigo';
    trend?: string;
    trendPositive?: boolean;
    onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ label, valore, icon, colorClass, trend, trendPositive, onClick }) => {
    const colors = {
        blue: { bg: 'bg-blue-50', text: 'text-blue-600', ring: 'ring-blue-100' },
        green: { bg: 'bg-green-50', text: 'text-green-600', ring: 'ring-green-100' },
        yellow: { bg: 'bg-amber-50', text: 'text-amber-600', ring: 'ring-amber-100' },
        red: { bg: 'bg-red-50', text: 'text-red-600', ring: 'ring-red-100' },
        purple: { bg: 'bg-purple-50', text: 'text-purple-600', ring: 'ring-purple-100' },
        indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', ring: 'ring-indigo-100' },
    };
    const theme = colors[colorClass] || colors.blue;
    return (
        <div onClick={onClick} className={`bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 ${onClick ? 'cursor-pointer' : ''}`}>
            <div className="flex justify-between items-start">
                <div><p className="text-sm font-medium text-gray-500 mb-1">{label}</p><h3 className={`text-2xl font-bold text-gray-900 tracking-tight`}>{valore}</h3></div>
                <div className={`p-3 rounded-xl ${theme.bg} ${theme.text}`}>{React.cloneElement(icon as React.ReactElement<any>, { size: 24 })}</div>
            </div>
            {trend && (<div className="mt-4 flex items-center text-xs font-medium"><span className={`flex items-center ${trendPositive ? 'text-green-600' : 'text-red-500'}`}>{trendPositive ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}{trend}</span><span className="text-gray-400 ml-2">vs mese scorso</span></div>)}
        </div>
    );
};

/**
 * True se l'incarico inizia oggi (data locale). Le giornate passate ancora aperte
 * restano in "Lavori di oggi" ma non sono di oggi.
 */
const isDiOggi = (inc: any): boolean => {
    if (!inc?.dataInizio) return false;
    const n = new Date();
    const today = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
    return String(inc.dataInizio).split('T')[0] === today;
};

const IMPORTI_VISIBILI_KEY = 'ud.dashboard.importiVisibili';
const leggiImportiVisibili = (): boolean => {
    try { return localStorage.getItem(IMPORTI_VISIBILI_KEY) === '1'; } catch { return false; }
};

export const DashboardView: React.FC = () => {
    const { user, currentSedeId } = useAuth();
    const navigate = useNavigate();
    const { addToast } = useToast();

    // Dati Dashboard
    const [stats, setStats] = useState({ praticheAperte: 0, praticheTotali: 0, clientiTotali: 0, sediTotali: 0, serviziAttivi: 0 });
    const [recentActivities, setRecentActivities] = useState<Pratica[]>([]);
    const [mieiIncarichiCount, setMieiIncarichiCount] = useState(0);

    // Dati finanziari (admin/responsabile)
    const [adminStats, setAdminStats] = useState<FinancialStats>({ fatturato: 0, incassato: 0, spese: 0, utile: 0, trend: 0 });
    const [totaleSpeseFisse, setTotaleSpeseFisse] = useState(0);
    const [praticheNonSaldate, setPraticheNonSaldate] = useState<any[]>([]);
    // Anche i servizi fiduciari con residuo entrano nel "Da Riscuotere", insieme
    // alle pratiche (GET /amministrazione/servizi-non-saldati).
    const [serviziNonSaldati, setServiziNonSaldati] = useState<any[]>([]);
    const [totaleCollaboratori, setTotaleCollaboratori] = useState(0);
    // Le uscite una tantum non sono in adminStats.spese: si sommano qui ai costi.
    const usciteUnaTantum = adminStats.usciteUnaTantum ?? 0;
    const utileReale = adminStats.incassato - adminStats.spese - totaleSpeseFisse - totaleCollaboratori - usciteUnaTantum;
    // Voci aperte = pratiche + servizi. Ognuna ha `_tipo` (PRATICA | SERVIZIO)
    // per scegliere la rotta corretta nella lista di dettaglio.
    const nonSaldatiAll = useMemo(() => {
        const pratiche = praticheNonSaldate.map(p => ({ ...p, _tipo: 'PRATICA' as const }));
        const servizi = serviziNonSaldati.map(s => ({ ...s, _tipo: 'SERVIZIO' as const }));
        return [...pratiche, ...servizi];
    }, [praticheNonSaldate, serviziNonSaldati]);
    const totaleDaRiscuotere = useMemo(
        () => nonSaldatiAll.reduce((acc, p) => acc + (p.importoResiduo || 0), 0),
        [nonSaldatiAll]
    );
    // Importi nascosti di default, così chi è accanto al titolare non li vede all'apertura.
    // La scelta resta in questo browser.
    const [importiVisibili, setImportiVisibili] = useState<boolean>(leggiImportiVisibili);
    const toggleImporti = () => setImportiVisibili(v => {
        try { localStorage.setItem(IMPORTI_VISIBILI_KEY, v ? '0' : '1'); } catch { /* storage non disponibile */ }
        return !v;
    });
    const formatCurrency = (n: number) => importiVisibili
        ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n)
        : '€ ***';

    // Periodo dei KPI mensili (1/3/6 mesi, 1 anno, dall'inizio, mesi a scelta),
    // default mese corrente. Fatturato, Incassato, Costi e Utile seguono il periodo;
    // "Da Riscuotere" è sempre il cumulato storico.
    type Preset = 'MESE' | 'MESI_3' | 'MESI_6' | 'ANNO' | 'INIZIO' | 'CUSTOM';
    const DASHBOARD_START_MONTH = '2026-06'; // Primo mese di utilizzo della piattaforma
    const todayMonthStr = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    };
    const monthsAgoStr = (n: number) => {
        const d = new Date();
        d.setMonth(d.getMonth() - n);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    };
    const ymToFirstDay = (ym: string) => `${ym}-01`;
    const ymToLastDay = (ym: string) => {
        const [y, m] = ym.split('-').map(Number);
        const last = new Date(y, m, 0).getDate();
        return `${ym}-${String(last).padStart(2, '0')}`;
    };
    const fmtMese = (ym: string) => {
        const [y, m] = ym.split('-').map(Number);
        const nomi = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
        return `${nomi[m - 1]} ${y}`;
    };
    const [preset, setPreset] = useState<Preset>('MESE');
    const [customFromMese, setCustomFromMese] = useState<string>(todayMonthStr());
    const [customToMese, setCustomToMese] = useState<string>(todayMonthStr());
    const periodo = useMemo(() => {
        switch (preset) {
            case 'MESE':   return { from: ymToFirstDay(todayMonthStr()), to: ymToLastDay(todayMonthStr()), label: fmtMese(todayMonthStr()) };
            case 'MESI_3': return { from: ymToFirstDay(monthsAgoStr(2)), to: ymToLastDay(todayMonthStr()), label: `${fmtMese(monthsAgoStr(2))} → ${fmtMese(todayMonthStr())}` };
            case 'MESI_6': return { from: ymToFirstDay(monthsAgoStr(5)), to: ymToLastDay(todayMonthStr()), label: `${fmtMese(monthsAgoStr(5))} → ${fmtMese(todayMonthStr())}` };
            case 'ANNO':   return { from: ymToFirstDay(monthsAgoStr(11)), to: ymToLastDay(todayMonthStr()), label: `${fmtMese(monthsAgoStr(11))} → ${fmtMese(todayMonthStr())}` };
            case 'INIZIO': return { from: ymToFirstDay(DASHBOARD_START_MONTH), to: ymToLastDay(todayMonthStr()), label: `${fmtMese(DASHBOARD_START_MONTH)} → ${fmtMese(todayMonthStr())}` };
            case 'CUSTOM': return { from: ymToFirstDay(customFromMese), to: ymToLastDay(customToMese), label: customFromMese === customToMese ? fmtMese(customFromMese) : `${fmtMese(customFromMese)} → ${fmtMese(customToMese)}` };
        }
    }, [preset, customFromMese, customToMese]);

    const [lavoriOggi, setLavoriOggi] = useState<any[]>([]);
    const [turniOggi, setTurniOggi] = useState<any[]>([]);
    // Widget "Da Approvare", da /api/approvazioni/summary.
    const [approvazioniSummary, setApprovazioniSummary] = useState({
        media: 0, lavori: 0, spese: 0, totale: 0,
    });
    // Banner "Hai N report da rifare" per il collaboratore: servono il conteggio
    // e id/incaricoId del primo per il deep-link.
    const [relazioniDaModificare, setRelazioniDaModificare] = useState<any[]>([]);
    // Banner "Hai N turni da accettare" (collaboratore).
    const [turniDaAccettare, setTurniDaAccettare] = useState<any[]>([]);
    // "Da Accettare" del collaboratore: incarichi ASSEGNATO ancora da decidere
    // (gli scaduti li rifiuta lo scheduler).
    const [incarichiDaAccettare, setIncarichiDaAccettare] = useState<any[]>([]);
    // Banner rifiuti degli ultimi 7 giorni (titolare/responsabile): incarichi e
    // turni insieme, i primi 5 nel banner e l'elenco completo in modale.
    const [rifiutiRecenti, setRifiutiRecenti] = useState<RifiutoRecenteDto[]>([]);
    const [showRifiutiModal, setShowRifiutiModal] = useState(false);
    // Widget escalation aperte (titolare/responsabile): notifiche MISS_* e
    // STOP_MAI_CHIUSO non confermate dal collaboratore.
    const [escalations, setEscalations] = useState<EscalationAlertDto[]>([]);
    // Modale "vedi tutte" paginata (escalation della settimana, senza dedup).
    const [showEscalationModal, setShowEscalationModal] = useState(false);
    const [escalationPage, setEscalationPage] = useState(1);
    const ESCALATION_PAGE_SIZE = 10;

    // Scadenze
    const [scadenze, setScadenze] = useState<ScadenzaDto[]>([]);
    const [isScadenzaModalOpen, setIsScadenzaModalOpen] = useState(false);
    const [newScadenza, setNewScadenza] = useState({ titolo: '', note: '', data: '', ora: '' });

    const [loading, setLoading] = useState(true);

    // Soglia per l'allerta orari difformi (da Impostazioni); null o 0 = nessun widget.
    const sogliaOrariMin = useSogliaOrariDifformi();

    // "Pulisci tutto (TEST)": solo ADMIN e solo se il backend ha
    // `app.test-reset.enabled=true`. Doppia conferma con la parola "CANCELLA".
    const [testResetEnabled, setTestResetEnabled] = useState(false);
    const [resettingTestData, setResettingTestData] = useState(false);
    useEffect(() => {
        if (user?.role !== UserRole.ADMIN) return;
        TestDataService.getStatus().then(s => setTestResetEnabled(!!s.enabled)).catch(() => {});
    }, [user]);

    // Relazioni da rifare del collaboratore, stesso endpoint del badge in sidebar.
    useEffect(() => {
        if (user?.role !== UserRole.COLLABORATORE) return;
        RelazioneGiornalieraService.getMieDaModificare()
            .then(list => setRelazioniDaModificare(Array.isArray(list) ? list : []))
            .catch(() => setRelazioniDaModificare([]));
        // Turni fiduciari da accettare per il banner.
        ServizioFiduciarioService.getMieiTurniDaAccettare()
            .then((res: any) => {
                const list = res?.data ?? (Array.isArray(res) ? res : []);
                setTurniDaAccettare(Array.isArray(list) ? list : []);
            })
            .catch(() => setTurniDaAccettare([]));
        // Gli incarichi da accettare vengono dalla stessa lista /me di loadData: una sola chiamata.
    }, [user]);

    const handleResetTestData = async () => {
        if (!window.confirm(
            'ATTENZIONE: questa azione cancellerà TUTTI i dati operativi (pratiche, incarichi, ' +
            'preventivi, relazioni, media, spese, audit log, clienti, scadenze).\n\n' +
            'Restano solo utenti, sedi, impostazioni, listini, qualifiche e template.\n\n' +
            'Procedere?'
        )) return;
        const confirm = window.prompt(
            'Per confermare digita esattamente: CANCELLA'
        );
        if (confirm !== 'CANCELLA') {
            addToast('Reset annullato', 'info');
            return;
        }
        setResettingTestData(true);
        try {
            const counts = await TestDataService.reset();
            const totale = Object.values(counts).reduce((a, n) => a + Math.max(0, Number(n) || 0), 0);
            addToast(`Reset eseguito: ${totale} record cancellati`, 'success');
            await loadData();
        } catch (err: any) {
            addToast(err?.response?.data?.message || 'Errore durante il reset', 'error');
        } finally {
            setResettingTestData(false);
        }
    };
    const lavoriDifformiOggi = useMemo(
        // Solo incarichi di oggi: "Lavori di oggi" contiene anche giornate passate
        // ancora aperte, che qui sembrerebbero di oggi.
        () => lavoriOggi.filter(l => isDiOggi(l) && hasOrariDifformi(l, sogliaOrariMin)),
        [lavoriOggi, sogliaOrariMin],
    );

    const loadData = async () => {
        if (!user) return;
        try {
            setLoading(true);

            // Caricamento in parallelo
            const [praticheRes, clientiRes, scadenzeRes, serviziRes, turniOggiRes] = await Promise.all([
                PraticaService.getAll({}),
                // I clienti sono riservati allo staff (al collaboratore il BE risponde 403):
                // per lui non facciamo la chiamata, così non compare il toast d'errore.
                (user.role !== UserRole.COLLABORATORE
                    ? ClientService.getAll().catch(() => ({ data: [] }))
                    : Promise.resolve({ data: [] })),
                ScadenzaService.getAll(),
                ServizioFiduciarioService.getAll().catch(() => ({ data: [] })),
                // Turni di oggi: servono anche al collaboratore per "I Miei Servizi Fiduciari Oggi".
                ServizioFiduciarioService.getTurniOggi().catch(() => ({ data: [] })),
            ]);

            const extractList = (res: any): any[] => {
                if (Array.isArray(res)) return res;
                if (Array.isArray(res?.data)) return res.data;
                if (Array.isArray(res?.data?.content)) return res.data.content;
                if (Array.isArray(res?.content)) return res.content;
                return [];
            };

            const listaPratiche = extractList(praticheRes);
            const listaClienti = extractList(clientiRes);
            const listaServizi = Array.isArray(serviziRes.data) ? serviziRes.data : (serviziRes.data as any).data || [];
            const listaTurniOggi = Array.isArray(turniOggiRes.data) ? turniOggiRes.data : [];
            // Filtro per sede del servizio (turno.servizio.sedeId). Il collaboratore vede
            // solo i propri turni, quindi per lui niente filtro sede.
            const turniPerSede = (user.role !== UserRole.COLLABORATORE && typeof currentSedeId === 'number')
                ? listaTurniOggi.filter((t: any) => t.servizio?.sedeId === currentSedeId)
                : listaTurniOggi;
            setTurniOggi(turniPerSede);

            const listaScadenze = extractList(scadenzeRes);
            setScadenze(listaScadenze);

            // Pratiche recenti
            const praticheOrdinate = [...listaPratiche].sort((a: Pratica, b: Pratica) => {
                const dataA = new Date(a.createdAt || a.ultimaAttivita || 0).getTime();
                const dataB = new Date(b.createdAt || b.ultimaAttivita || 0).getTime();
                return dataB - dataA;
            });
            setRecentActivities(praticheOrdinate.slice(0, 5));

            // Pratiche aperte
            const aperte = listaPratiche.filter((p: Pratica) => {
                const s = (p.stato || '').toUpperCase();
                return s.includes('CORSO') || s.includes('APERTA') || s === 'OPEN' || s === 'ACTIVE';
            }).length;

            // Sedi (solo admin)
            let countSedi = 0;
            if (user.role === UserRole.ADMIN) {
                const sediRes = await SedeService.getAll();
                countSedi = (sediRes as any).data?.length || 0;
            }

            setStats({
                praticheAperte: aperte,
                praticheTotali: listaPratiche.length,
                clientiTotali: listaClienti.length,
                sediTotali: countSedi,
                serviziAttivi: listaServizi.length
            });

            // Lavori di oggi
            try {
                // Il collaboratore usa /incarichi/me, già limitato ai suoi incarichi e con
                // pochi campi, invece di scaricare tutto e filtrare lato client.
                // Admin e responsabile usano getAll per la vista globale con filtro sede.
                const incarichiRes = user.role === UserRole.COLLABORATORE
                    ? await IncaricoService.getMyIncarichi()
                    : await IncaricoService.getAll();
                const incarichiList = Array.isArray(incarichiRes) ? incarichiRes : (incarichiRes as any).data || [];

                // Con /me il collaboratore ha già solo i suoi incarichi.
                if (user.role === UserRole.COLLABORATORE) {
                    setMieiIncarichiCount(incarichiList.length);
                    setIncarichiDaAccettare(incarichiList.filter((i: any) => i.stato === 'ASSEGNATO'));
                }

                // Data di oggi in formato YYYY-MM-DD
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                const todayStr = `${year}-${month}-${day}`;

                const oggi = incarichiList.filter((inc: any) => {
                    // Collaboratore: /incarichi/me è già filtrato, nessun filtro per collaboratore.

                    // Filtro sulla sede del mandato (incarico.pratica.sedeId), coerente con i KPI.
                    // Il collaboratore può lavorare su più sedi, quindi per lui niente filtro.
                    if (user.role !== UserRole.COLLABORATORE && typeof currentSedeId === 'number') {
                        if (inc.pratica?.sedeId !== currentSedeId) return false;
                    }

                    if (inc.stato === 'ANNULLATO') return false;
                    // Gli incarichi revocati non vanno nella pianificazione di oggi.
                    if (inc.stato === 'REVOCATO') return false;
                    // Quelli da accettare sono già nel box "Da Accettare"; dopo l'accettazione
                    // compaiono qui.
                    if (inc.stato === 'ASSEGNATO') return false;
                    // Sessione GPS ancora aperta (Start senza Stop) di qualunque data: resta in
                    // dashboard così il collaboratore può fare Stop anche dopo dataFine.
                    if (inc.activeSessionId) return true;
                    if (!inc.dataInizio) return false;

                    const startStr = inc.dataInizio.split('T')[0];
                    const endStr = inc.dataFine ? inc.dataFine.split('T')[0] : startStr;

                    // In "Lavori di oggi" vanno le attività che iniziano e finiscono oggi,
                    // non le autorizzazioni lunghe mesi.
                    if (startStr === todayStr && endStr === todayStr) return true;
                    // Giornata conclusa ma report ancora da inviare: resta qui, come in
                    // getIncaricoCategory e nell'app. Serve almeno una sessione chiusa, nessuna
                    // relazione e uno stato non terminale.
                    if (endStr && endStr < todayStr) {
                        const hasClosedSession = (inc.sessioni || []).some((s: any) => !!s.endTime);
                        const noReportYet = !inc.relazioni || inc.relazioni.length === 0;
                        const notTerminal = inc.stato !== 'REVOCATO' && inc.stato !== 'RIFIUTATO' && inc.stato !== 'COMPLETATO';
                        if (hasClosedSession && noReportYet && notTerminal) return true;
                    }
                    return false;
                });

                // Ordine per orario di inizio
                oggi.sort((a: any, b: any) => new Date(a.dataInizio).getTime() - new Date(b.dataInizio).getTime());

                setLavoriOggi(oggi);
            } catch (err) { console.warn(err); }

            // I dati finanziari si caricano in un useEffect separato che segue anche il
            // cambio di periodo, per non fare doppie chiamate al mount.

            // Riepilogo approvazioni (solo ADMIN/RESPONSABILE).
            if (user.role !== UserRole.COLLABORATORE) {
                try {
                    const summary = await ApprovazioniService.getSummary(currentSedeId || 'ALL');
                    setApprovazioniSummary(summary);
                } catch (err) { /* Widget facoltativo, errori ignorati */ }

                // Rifiuti recenti per il banner, facoltativo: errori ignorati.
                try {
                    const rifiuti = await ApprovazioniService.getRifiutiRecenti(currentSedeId || 'ALL', 7);
                    setRifiutiRecenti(rifiuti);
                } catch (err) { setRifiutiRecenti([]); }

                // Escalation aperte. L'ADMIN passa la sede selezionata (null = tutte);
                // per il RESPONSABILE il BE forza comunque la sua sede.
                try {
                    const esc = await EscalationAlertService.list(7, typeof currentSedeId === 'number' ? currentSedeId : null);
                    setEscalations(esc);
                } catch (err) { setEscalations([]); }
            }

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, [user, currentSedeId]);

    // Ricarica i turni di oggi quando la dashboard torna in primo piano, così dopo
    // uno Start fatto altrove il badge si aggiorna. Solo i turni, non tutto loadData.
    useEffect(() => {
        if (!user) return;
        // focus e visibilitychange arrivano spesso insieme: una richiesta alla volta e
        // non più di una ogni pochi secondi.
        let inFlight = false;
        let lastAt = 0;
        const refreshTurni = async () => {
            if (inFlight || Date.now() - lastAt < 5_000) return;
            inFlight = true;
            lastAt = Date.now();
            try {
                const res = await ServizioFiduciarioService.getTurniOggi();
                const lista = Array.isArray(res.data) ? res.data : [];
                const filtrati = (user.role !== UserRole.COLLABORATORE && typeof currentSedeId === 'number')
                    ? lista.filter((t: any) => t.servizio?.sedeId === currentSedeId)
                    : lista;
                setTurniOggi(filtrati);
            } catch { /* Refresh silenzioso: al massimo resta lo stato precedente */ }
            finally { inFlight = false; }
        };
        const onFocus = () => refreshTurni();
        const onVisibility = () => { if (document.visibilityState === 'visible') refreshTurni(); };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [user, currentSedeId]);

    // KPI finanziari, spese fisse e costi collaboratori: seguono sede e periodo.
    // "Da Riscuotere" non dipende dal periodo ma lo carichiamo qui insieme agli altri.
    useEffect(() => {
        if (!user || user.role === UserRole.COLLABORATORE) return;
        const sedeId = currentSedeId ?? 'ALL';
        const { from, to } = periodo;
        let cancelled = false;
        (async () => {
            try {
                const [statsRes, speseFisseRes, collabRes, nonSaldateRes, serviziNonSaldatiRes] = await Promise.all([
                    AmministrazioneService.getStats(sedeId, from, to),
                    AmministrazioneService.getSpeseFisseRange(from, to, sedeId),
                    AmministrazioneService.getCostiCollaboratoriRange(from, to, sedeId),
                    AmministrazioneService.getPraticheNonSaldate(sedeId),
                    // Servizi fiduciari con residuo in parallelo: stesso DTO delle pratiche,
                    // uniti in `nonSaldatiAll`.
                    AmministrazioneService.getServiziNonSaldati(sedeId),
                ]);
                if (cancelled) return;
                if (statsRes.success) setAdminStats(statsRes.data);
                setTotaleSpeseFisse(speseFisseRes?.data ?? 0);
                setTotaleCollaboratori(collabRes?.data ?? 0);
                if (nonSaldateRes.success) setPraticheNonSaldate(nonSaldateRes.data || []);
                if (serviziNonSaldatiRes.success) setServiziNonSaldati(serviziNonSaldatiRes.data || []);
            } catch (err) {
                if (!cancelled) console.warn('Errore dati finanziari:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [user, currentSedeId, periodo]);

    const handleAddScadenza = async () => {
        if (!newScadenza.titolo) {
            addToast("Il titolo è obbligatorio", "error");
            return;
        }

        try {
            // Il BE usa LocalDateTime senza fuso: inviamo l'orario locale così com'è.
            // toISOString() lo convertirebbe in UTC spostando il promemoria di 1-2 ore.
            let isoDate: string | undefined = undefined;
            if (newScadenza.data) {
                const time = newScadenza.ora || "00:00";
                isoDate = `${newScadenza.data}T${time}:00`;
            }

            await ScadenzaService.create({
                titolo: newScadenza.titolo,
                note: newScadenza.note,
                dataScadenza: isoDate
            });

            addToast("Scadenza aggiunta con successo", "success");
            setIsScadenzaModalOpen(false);
            setNewScadenza({ titolo: '', note: '', data: '', ora: '' });
            loadData();
        } catch (err) {
            console.error(err);
            addToast("Errore durante il salvataggio della scadenza", "error");
        }
    };

    const handleCompleteScadenza = async (id: number) => {
        try {
            await ScadenzaService.complete(id);
            addToast("Scadenza segnata come completata", "success");
            loadData();
        } catch (err) {
            console.error(err);
            addToast("Errore durante l'aggiornamento", "error");
        }
    };

    const handleDeleteScadenza = async (id: number) => {
        if (!window.confirm("Sei sicuro di voler eliminare questa scadenza?")) return;
        try {
            await ScadenzaService.delete(id);
            addToast("Scadenza eliminata", "success");
            loadData();
        } catch (err) {
            console.error(err);
            addToast("Errore durante l'eliminazione", "error");
        }
    };

    const formatDate = (isoString?: string) => {
        if (!isoString) return null;
        const d = new Date(isoString);
        return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    };

    const isExpired = (isoString?: string) => {
        if (!isoString) return false;
        return new Date(isoString) < new Date();
    };

    const getClientDisplay = (p: Pratica) => {
        if (p.clientName) return p.clientName;
        if (p.cliente) return p.cliente;
        if (p.client) {
            const c = Array.isArray(p.client) ? p.client[0] : p.client;
            if (c) return (c as any).ragioneSociale || `${(c as any).nome || ''} ${(c as any).surname || ''}`.trim();
        }
        return 'Cliente non specificato';
    };

    const getPraticaName = (p: Pratica) => p.name || p.nome || 'Pratica senza nome';

    const getStatusColor = (stato?: string) => {
        const s = (stato || '').toUpperCase();
        if (s.includes('CORSO') || s.includes('APERTA')) return 'bg-green-100 text-green-700 border-green-200';
        if (s.includes('CHIUSA') || s.includes('TERMINATA')) return 'bg-gray-100 text-gray-600 border-gray-200';
        if (s.includes('SOSPESA')) return 'bg-amber-100 text-amber-700 border-amber-200';
        return 'bg-blue-50 text-blue-700 border-blue-200';
    };

    if (loading) return <div className="flex h-screen items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

    return (
        <div className="p-6 md:p-8 space-y-8 bg-gray-50 min-h-screen font-sans">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Dashboard</h1>
                    <p className="text-gray-500 mt-1">Bentornato, <span className="text-gray-900 font-medium">{user?.name}</span>.</p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-600 bg-white px-4 py-2 rounded-full border border-gray-200 shadow-sm">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>

                    {/*
                      Reset dati di test: solo ADMIN e solo se abilitato lato BE (disattivo in
                      produzione). Doppia conferma con la parola "CANCELLA".
                    */}
                    {user?.role === UserRole.ADMIN && testResetEnabled && (
                        <button
                            onClick={handleResetTestData}
                            disabled={resettingTestData}
                            className="flex items-center gap-2 text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 px-4 py-2 rounded-full shadow-sm transition-colors"
                            title="Cancella tutti i dati operativi (test): pratiche, incarichi, preventivi, relazioni, media, spese, clienti."
                        >
                            <Trash2 size={16}/> {resettingTestData ? 'Pulizia in corso…' : 'Pulisci tutto (TEST)'}
                        </button>
                    )}
                </div>
            </div>

            {/*
              Dashboard collaboratore: lavori di oggi (turni e incarichi) e quelli da
              accettare. Le sezioni sotto restano per la gestione operativa.
            */}
            {user?.role === UserRole.COLLABORATORE && (() => {
                // /servizi/turni/oggi è già filtrato dal BE per il collaboratore e non contiene
                // il campo collaboratore, quindi non si filtra su collaboratore.id.
                const miei = turniOggi.filter((t: any) => t.statoAccettazione !== 'RIFIUTATO' && t.statoAccettazione !== 'ASSEGNATO' && !t.dataRevoca);
                const fmtOraTurno = (t: any) => `${t.oraInizio || '?'} – ${t.oraFine || '?'}`;
                const fmtOraIncarico = (inc: any) => {
                    const s = inc.dataInizio ? new Date(inc.dataInizio).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '';
                    const e = inc.dataFine ? new Date(inc.dataFine).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '';
                    return s && e ? `${s} – ${e}` : (s || e || '');
                };
                const titoloIncarico = (inc: any) => {
                    const praticaName = (inc.pratica as any)?.name || (inc.pratica as any)?.nome || '';
                    const numMandato = praticaName.includes(' - ') ? praticaName.split(' - ')[0] : praticaName;
                    const soggetto = inc.soggettoNome || '';
                    return soggetto ? `${numMandato} - ${soggetto}` : (numMandato || `Incarico #${inc.id}`);
                };
                const totOggi = miei.length + lavoriOggi.length;
                const totDaAccettare = turniDaAccettare.length + incarichiDaAccettare.length;
                return (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {/* Oggi */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 border-l-4 border-l-indigo-500 p-5">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-sm font-black uppercase tracking-wider text-slate-700 flex items-center gap-2">
                                    <CalendarCheck className="w-5 h-5 text-indigo-600"/>
                                    Lavori di Oggi
                                </h3>
                                {totOggi > 0 && (
                                    <span className="text-[11px] font-black text-indigo-700 bg-indigo-100 border border-indigo-200 px-2.5 py-1 rounded-full min-w-[28px] text-center">
                                        {totOggi}
                                    </span>
                                )}
                            </div>
                            {totOggi === 0 ? (
                                <div className="bg-gray-50 p-4 rounded-lg border border-dashed border-gray-200 text-center text-xs text-gray-500">
                                    Oggi non hai lavori programmati.
                                </div>
                            ) : (
                                <ul className="space-y-2">
                                    {miei.map((t: any) => (
                                        <li
                                            key={`t-${t.id}`}
                                            onClick={() => navigate('/incarichi', { state: { openTurnoId: t.id } })}
                                            className="flex items-center gap-3 p-2.5 rounded-lg border border-emerald-100 bg-emerald-50/40 hover:bg-emerald-50 hover:border-emerald-200 cursor-pointer transition-colors"
                                        >
                                            <Shield className="w-4 h-4 text-emerald-600 shrink-0"/>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-bold text-slate-900 truncate">{t.servizio?.nome || 'Servizio fiduciario'}</div>
                                                <div className="text-[10px] text-slate-500 font-medium font-mono">{fmtOraTurno(t)}</div>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-slate-300"/>
                                        </li>
                                    ))}
                                    {lavoriOggi.map((inc: any) => (
                                        <li
                                            key={`i-${inc.id}`}
                                            onClick={() => navigate('/incarichi', { state: { openIncaricoId: inc.id } })}
                                            className="flex items-center gap-3 p-2.5 rounded-lg border border-indigo-100 bg-indigo-50/40 hover:bg-indigo-50 hover:border-indigo-200 cursor-pointer transition-colors"
                                        >
                                            <Activity className="w-4 h-4 text-indigo-600 shrink-0"/>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-bold text-slate-900 truncate">{titoloIncarico(inc)}</div>
                                                <div className="text-[10px] text-slate-500 font-medium font-mono">{fmtOraIncarico(inc) || (inc.luogo || '—')}</div>
                                            </div>
                                            {/* Badge "Report" per la giornata conclusa con il report ancora da inviare. */}
                                            {(() => {
                                                const _n = new Date();
                                                const tStr = `${_n.getFullYear()}-${String(_n.getMonth() + 1).padStart(2, '0')}-${String(_n.getDate()).padStart(2, '0')}`;
                                                const eStr = inc.dataFine ? inc.dataFine.split('T')[0] : '';
                                                const isReport = !!eStr && eStr < tStr
                                                    && (inc.sessioni || []).some((s: any) => !!s.endTime)
                                                    && (!inc.relazioni || inc.relazioni.length === 0)
                                                    && inc.stato !== 'REVOCATO' && inc.stato !== 'RIFIUTATO' && inc.stato !== 'COMPLETATO';
                                                return isReport ? (
                                                    <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200 whitespace-nowrap shrink-0">
                                                        Report
                                                    </span>
                                                ) : null;
                                            })()}
                                            <ChevronRight className="w-4 h-4 text-slate-300"/>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Da accettare, sempre visibile */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 border-l-4 border-l-amber-500 p-5">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-sm font-black uppercase tracking-wider text-slate-700 flex items-center gap-2">
                                    <CalendarDays className="w-5 h-5 text-amber-600"/>
                                    Da Accettare
                                </h3>
                                {totDaAccettare > 0 && (
                                    <span className="text-[11px] font-black text-amber-700 bg-amber-100 border border-amber-200 px-2.5 py-1 rounded-full min-w-[28px] text-center">
                                        {totDaAccettare}
                                    </span>
                                )}
                            </div>
                            {totDaAccettare === 0 ? (
                                <div className="bg-gray-50 p-4 rounded-lg border border-dashed border-gray-200 text-center text-xs text-gray-500">
                                    Nessun incarico o turno in attesa di accettazione.
                                </div>
                            ) : (
                                <ul className="space-y-2">
                                    {turniDaAccettare.map((t: any) => (
                                        <li
                                            key={`tda-${t.id}`}
                                            onClick={() => navigate('/incarichi', { state: { openTurnoId: t.id } })}
                                            className="flex items-center gap-3 p-2.5 rounded-lg border border-amber-100 bg-amber-50/40 hover:bg-amber-50 hover:border-amber-200 cursor-pointer transition-colors"
                                        >
                                            <Shield className="w-4 h-4 text-amber-600 shrink-0"/>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-bold text-slate-900 truncate">{t.servizio?.nome || 'Servizio fiduciario'}</div>
                                                <div className="text-[10px] text-slate-500 font-medium">
                                                    {t.dataTurno ? new Date(t.dataTurno).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}{'  '}
                                                    <span className="font-mono">{fmtOraTurno(t)}</span>
                                                </div>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-slate-300"/>
                                        </li>
                                    ))}
                                    {incarichiDaAccettare.map((inc: any) => (
                                        <li
                                            key={`ida-${inc.id}`}
                                            onClick={() => navigate('/incarichi', { state: { openIncaricoId: inc.id } })}
                                            className="flex items-center gap-3 p-2.5 rounded-lg border border-amber-100 bg-amber-50/40 hover:bg-amber-50 hover:border-amber-200 cursor-pointer transition-colors"
                                        >
                                            <Activity className="w-4 h-4 text-amber-600 shrink-0"/>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-bold text-slate-900 truncate">{titoloIncarico(inc)}</div>
                                                <div className="text-[10px] text-slate-500 font-medium">
                                                    {inc.dataInizio ? new Date(inc.dataInizio).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}{'  '}
                                                    <span className="font-mono">{fmtOraIncarico(inc)}</span>
                                                </div>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-slate-300"/>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/*
              KPI finanziari, solo admin/responsabile: il collaboratore non accede ai
              mandati e i suoi incarichi sono già nelle sezioni sotto.
            */}
            {user?.role !== UserRole.COLLABORATORE && (
                <>
                    {/*
                      Periodo, con gli stessi preset di Registro Movimenti. In modalità personalizzata
                      si scelgono mesi (non giorni) perché i KPI sono mensili. "Da Riscuotere" non
                      dipende dal periodo.
                    */}
                    <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
                            <CalendarIcon className="w-3.5 h-3.5" /> Periodo
                        </span>
                        {([
                            ['MESE', '1 mese'],
                            ['MESI_3', '3 mesi'],
                            ['MESI_6', '6 mesi'],
                            ['ANNO', '1 anno'],
                            ['INIZIO', "Dall'inizio"],
                            ['CUSTOM', 'Personalizzato'],
                        ] as [Preset, string][]).map(([k, label]) => (
                            <button
                                key={k}
                                onClick={() => setPreset(k)}
                                className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                                    preset === k
                                        ? 'bg-slate-800 text-white border-slate-800'
                                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                        {preset === 'CUSTOM' && (
                            <>
                                <input
                                    type="month"
                                    value={customFromMese}
                                    min={DASHBOARD_START_MONTH}
                                    max={todayMonthStr()}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (!v) return;
                                        setCustomFromMese(v);
                                        // Se "da" supera "a" spostiamo "a" in avanti, altrimenti i mesi successivi
                                        // non sarebbero più selezionabili.
                                        if (customToMese && v > customToMese) setCustomToMese(v);
                                    }}
                                    className="ml-2 px-2 py-1 border border-slate-200 rounded-md text-xs text-slate-700"
                                />
                                <span className="text-slate-400 text-xs">→</span>
                                <input
                                    type="month"
                                    value={customToMese}
                                    min={DASHBOARD_START_MONTH}
                                    max={todayMonthStr()}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (!v) return;
                                        setCustomToMese(v);
                                        // Viceversa: se "a" precede "da", riportiamo indietro "da".
                                        if (customFromMese && v < customFromMese) setCustomFromMese(v);
                                    }}
                                    className="px-2 py-1 border border-slate-200 rounded-md text-xs text-slate-700"
                                />
                            </>
                        )}
                        <span className="ml-auto text-[11px] text-slate-400 whitespace-nowrap capitalize">{periodo.label}</span>
                        <button
                            type="button"
                            onClick={toggleImporti}
                            className="px-2.5 py-1 text-xs rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5"
                            title={importiVisibili ? 'Nascondi gli importi' : 'Mostra gli importi'}
                        >
                            {importiVisibili ? <EyeOff className="w-3.5 h-3.5"/> : <Eye className="w-3.5 h-3.5"/>}
                            {importiVisibili ? 'Nascondi' : 'Mostra'}
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                        {/* Fatturato */}
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-4 opacity-5"><DollarSign size={72} className="text-blue-600"/></div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Fatturato</p>
                            <p className="text-3xl font-bold text-gray-900">{formatCurrency(adminStats.fatturato)}</p>
                            <span className="text-xs text-gray-400 font-medium mt-1 block capitalize">{periodo.label}</span>
                        </div>
                        {/* Incassato */}
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-emerald-200 relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-4 opacity-5"><DollarSign size={72} className="text-emerald-600"/></div>
                            <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">Incassato</p>
                            <p className="text-3xl font-bold text-emerald-700">{formatCurrency(adminStats.incassato ?? 0)}</p>
                            <span className="text-xs text-gray-400 font-medium mt-1 block capitalize">{periodo.label}</span>
                        </div>
                        {/*
                          Costi totali, comprese le spese anticipate dall'azienda (adminStats.spese),
                          così il box resta allineato all'utile netto. La sotto-voce "carta aziendale"
                          mostra le spese pagate dall'azienda: spese collaboratore approvate e spese
                          dei report con anticipatoAzienda=true.
                        */}
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Costi Totali</p>
                            <p className="text-3xl font-bold text-gray-900">{formatCurrency(adminStats.spese + totaleSpeseFisse + totaleCollaboratori + usciteUnaTantum)}</p>
                            <div className="mt-1 space-y-0.5">
                                <span className="text-xs text-gray-400 font-medium block">{formatCurrency(totaleSpeseFisse)} fissi</span>
                                <span className="text-xs text-emerald-600 font-medium block">{formatCurrency(adminStats.spese)} carta aziendale</span>
                                <span className="text-xs text-indigo-500 font-semibold block">{formatCurrency(totaleCollaboratori)} collaboratori</span>
                                {usciteUnaTantum > 0 && (
                                    <span className="text-xs text-red-500 font-medium block">{formatCurrency(usciteUnaTantum)} una tantum</span>
                                )}
                            </div>
                        </div>
                        {/* Utile Netto */}
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Utile Netto Reale</p>
                            <p className={`text-3xl font-bold ${!importiVisibili ? 'text-gray-900' : utileReale >= 0 ? 'text-blue-600' : 'text-red-600'}`}>{formatCurrency(utileReale)}</p>
                            <span className="text-xs text-gray-400 font-medium mt-1 block">Al netto di tutte le spese</span>
                        </div>
                        {/* Da Riscuotere: sempre cumulato storico, non dipende dal periodo */}
                        <div
                            className="bg-white p-6 rounded-2xl shadow-sm border border-orange-200 relative overflow-hidden cursor-pointer hover:shadow-md transition-all"
                            onClick={() => navigate('/amministrazione')}
                        >
                            <div className="absolute top-0 right-0 p-4 opacity-5"><TrendingUp size={72} className="text-orange-500"/></div>
                            <p className="text-xs font-bold text-orange-500 uppercase tracking-wider mb-1">Da Riscuotere</p>
                            <p className="text-3xl font-bold text-orange-600">{formatCurrency(totaleDaRiscuotere)}</p>
                            <span className="text-xs text-gray-400 font-medium mt-1 block">
                                {praticheNonSaldate.length} {praticheNonSaldate.length === 1 ? 'pratica' : 'pratiche'}
                                {' + '}
                                {serviziNonSaldati.length} {serviziNonSaldati.length === 1 ? 'servizio' : 'servizi'}
                                {' (storico)'}
                            </span>
                        </div>
                    </div>
                </>
            )}

            {/*
              Banner rifiuti degli ultimi 7 giorni per titolare/responsabile: al massimo
              5 righe, "Vedi tutti" apre l'elenco completo (rifiuti del collaboratore o
              automatici dello scheduler).
            */}
            {user?.role !== UserRole.COLLABORATORE && rifiutiRecenti.length > 0 && (() => {
                const top = rifiutiRecenti.slice(0, 5);
                const fmtTs = (s: string | null | undefined) => {
                    if (!s) return '—';
                    try {
                        return new Date(s).toLocaleString('it-IT', {
                            day: '2-digit', month: '2-digit',
                            hour: '2-digit', minute: '2-digit',
                        });
                    } catch { return s; }
                };
                const goToEntity = (r: RifiutoRecenteDto) => {
                    if (r.tipo === 'INCARICO' && r.mandatoId) navigate(`/pratiche/${r.mandatoId}`);
                    else if (r.tipo === 'TURNO' && r.servizioId) navigate(`/servizi/${r.servizioId}`);
                };
                return (
                    <div className="bg-white border-2 border-rose-200 rounded-2xl shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between gap-3 px-5 py-3 bg-gradient-to-r from-rose-50 to-white border-b border-rose-200">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center">
                                    <XCircle size={18}/>
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-rose-900">Rifiuti degli ultimi 7 giorni</p>
                                    <p className="text-[11px] text-rose-700/80 font-medium">
                                        {rifiutiRecenti.length} {rifiutiRecenti.length === 1 ? 'rifiuto registrato' : 'rifiuti registrati'} da incarichi e turni
                                    </p>
                                </div>
                            </div>
                            {rifiutiRecenti.length > top.length && (
                                <button
                                    onClick={() => setShowRifiutiModal(true)}
                                    className="text-xs font-bold text-rose-700 hover:text-rose-900 bg-white border border-rose-200 hover:border-rose-400 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                                >
                                    Vedi tutti <ChevronRight size={14}/>
                                </button>
                            )}
                            {rifiutiRecenti.length <= top.length && rifiutiRecenti.length > 0 && (
                                <button
                                    onClick={() => setShowRifiutiModal(true)}
                                    className="text-xs font-bold text-rose-700 hover:text-rose-900 bg-white border border-rose-200 hover:border-rose-400 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                                >
                                    Dettagli <ChevronRight size={14}/>
                                </button>
                            )}
                        </div>
                        <div className="divide-y divide-gray-100">
                            {top.map(r => (
                                <div
                                    key={`${r.tipo}-${r.entityId}`}
                                    onClick={() => goToEntity(r)}
                                    className="px-5 py-3 flex items-center gap-3 hover:bg-rose-50/40 cursor-pointer transition-colors"
                                >
                                    <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${r.tipo === 'INCARICO' ? 'bg-blue-100 text-blue-700' : 'bg-indigo-100 text-indigo-700'}`}>
                                        {r.tipo === 'INCARICO' ? 'Incarico' : 'Turno'}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-2 flex-wrap">
                                            <span className="font-bold text-sm text-gray-900 truncate">{r.riferimento}</span>
                                            <span className="text-xs text-gray-500">· {r.collaboratore}</span>
                                        </div>
                                        {r.motivo && (
                                            <p className="text-[11px] text-gray-600 italic truncate">"{r.motivo}"</p>
                                        )}
                                    </div>
                                    <span className="text-[11px] font-semibold text-rose-700 whitespace-nowrap">
                                        {fmtTs(r.dataRifiuto)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })()}

            {/*
              Escalation aperte: notifiche MISS_* e STOP_MAI_CHIUSO non confermate.
              Per ADMIN/RESPONSABILE, già filtrate per sede dal BE.
            */}
            {user?.role !== UserRole.COLLABORATORE && escalations.length > 0 && (() => {
                const fmtTs = (s: string | null | undefined) => {
                    if (!s) return '—';
                    try {
                        return new Date(s).toLocaleString('it-IT', {
                            day: '2-digit', month: '2-digit',
                            hour: '2-digit', minute: '2-digit',
                        });
                    } catch { return s; }
                };
                const goToEntity = (r: EscalationAlertDto) => {
                    if (r.entity === 'incarico') navigate(`/pratiche?focusIncarico=${r.entityId}`);
                    else navigate(`/servizi?focusTurno=${r.entityId}`);
                };
                const sevTheme = (sev: EscalationAlertDto['severita']) => {
                    if (sev === 'CRITICAL') return { bg: 'bg-red-100', text: 'text-red-700', label: 'CRITICA' };
                    if (sev === 'HIGH') return { bg: 'bg-orange-100', text: 'text-orange-700', label: 'ALTA' };
                    // INFO = avviso 1h prima (PRE_START): stile neutro, non un allarme.
                    if (sev === 'INFO') return { bg: 'bg-sky-100', text: 'text-sky-700', label: 'AVVISO' };
                    return { bg: 'bg-amber-100', text: 'text-amber-700', label: 'MEDIA' };
                };
                const tipoLabel = (tipo: string) => {
                    if (tipo === 'STOP_MAI_CHIUSO') return 'Servizio mai chiuso';
                    if (tipo === 'PRE_START_60MIN') return 'Avviso 1 ora prima';
                    if (tipo.startsWith('MISS_START_')) return `Mancato avvio (${tipo.split('_').pop()}m)`;
                    if (tipo.startsWith('MISS_STOP_')) return `Mancata chiusura (${tipo.split('_').pop()}m)`;
                    if (tipo.startsWith('MISS_REPORT_')) return `Manca relazione (${tipo.split('_').pop()})`;
                    return tipo;
                };
                // Nel riquadro solo le escalation ancora aperte; le risolte restano in "Vedi tutte".
                // Dedup per collaboratore + entity + entityId. L'elenco arriva ordinato per
                // sentAt DESC, quindi la prima occorrenza è la più recente.
                const dedupKey = (r: EscalationAlertDto) =>
                    `${r.collaboratoreId ?? 0}-${r.entity}-${r.entityId}`;
                const seen = new Set<string>();
                const deduped: EscalationAlertDto[] = [];
                for (const r of escalations) {
                    if (r.statoAttuale === 'RISOLTO') continue;
                    const k = dedupKey(r);
                    if (seen.has(k)) continue;
                    seen.add(k);
                    deduped.push(r);
                }
                const renderRow = (r: EscalationAlertDto) => {
                    const sev = sevTheme(r.severita);
                    return (
                        <div
                            key={`${r.entity}-${r.notificaId}`}
                            className="px-5 py-3 flex items-center gap-3 hover:bg-red-50/40 transition-colors"
                        >
                            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${r.entity === 'incarico' ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                {r.entity === 'incarico' ? 'Incarico' : 'Turno'}
                            </span>
                            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${sev.bg} ${sev.text}`}>
                                {sev.label}
                            </span>
                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => goToEntity(r)}>
                                <div className="flex items-baseline gap-2 flex-wrap">
                                    <span className="font-bold text-sm text-gray-900 truncate">{r.riferimento}</span>
                                    {r.collaboratoreNome && (
                                        <span className="text-xs text-gray-500">· {r.collaboratoreNome}</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-[11px] text-gray-600 italic truncate">{tipoLabel(r.tipo)}</p>
                                    {/*
                                      APERTO/RISOLTO vale solo per le anomalie; per l'avviso 1h conta solo
                                      letto/non letto (badge azzurro sotto).
                                    */}
                                    {r.tipo !== 'PRE_START_60MIN' && (
                                        r.statoAttuale === 'RISOLTO' ? (
                                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 whitespace-nowrap">
                                                Risolto {fmtTs(r.risoltoAt)}
                                            </span>
                                        ) : (
                                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-red-100 text-red-700 whitespace-nowrap">
                                                Ancora aperto
                                            </span>
                                        )
                                    )}
                                    {/*
                                      Stato di lettura: "Letto alle HH:MM" se il collaboratore ha confermato dalla
                                      push, altrimenti "Non letto", così il titolare sa se va richiamato.
                                    */}
                                    {r.ackAt ? (
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-sky-100 text-sky-700 whitespace-nowrap">
                                            Letto {fmtTs(r.ackAt)}
                                        </span>
                                    ) : (
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-500 whitespace-nowrap">
                                            Non letto
                                        </span>
                                    )}
                                </div>
                            </div>
                            {r.collaboratoreTel && (
                                <a
                                    href={`tel:${r.collaboratoreTel}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-[11px] font-bold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                                >
                                    Chiama
                                </a>
                            )}
                            <span className="text-[11px] font-semibold text-red-700 whitespace-nowrap">
                                {fmtTs(r.sentAt)}
                            </span>
                        </div>
                    );
                };

                // Modale paginata (10 per pagina) con tutte le escalation della settimana,
                // senza dedup. Paginazione lato client: il BE manda già 7 giorni filtrati per sede.
                const totalPages = Math.max(1, Math.ceil(escalations.length / ESCALATION_PAGE_SIZE));
                const currentPage = Math.min(escalationPage, totalPages);
                const pageStart = (currentPage - 1) * ESCALATION_PAGE_SIZE;
                const pageRows = escalations.slice(pageStart, pageStart + ESCALATION_PAGE_SIZE);

                return (
                    <>
                        <div className="bg-white border-2 border-red-200 rounded-2xl shadow-sm overflow-hidden">
                            <div className="flex items-center justify-between gap-3 px-5 py-3 bg-gradient-to-r from-red-50 to-white border-b border-red-200">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-xl bg-red-100 text-red-700 flex items-center justify-center">
                                        <ShieldAlert size={18}/>
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-red-900">Escalation aperte</p>
                                        <p className="text-[11px] text-red-700/80 font-medium">
                                            {deduped.length} {deduped.length === 1 ? 'servizio senza conferma' : 'servizi senza conferma'} dal collaboratore
                                            <span className="text-red-500/70"> · {escalations.length} notifiche negli ultimi 7 giorni</span>
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => { setEscalationPage(1); setShowEscalationModal(true); }}
                                    className="text-[11px] font-bold text-red-700 hover:text-red-900 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap flex items-center gap-1"
                                >
                                    Vedi tutte
                                    <ChevronRight size={14}/>
                                </button>
                            </div>
                            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                                {deduped.length === 0 ? (
                                    <p className="px-5 py-4 text-sm text-gray-500">Nessuna escalation aperta.</p>
                                ) : deduped.map(renderRow)}
                            </div>
                        </div>

                        {showEscalationModal && (
                            <div
                                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
                                onClick={() => setShowEscalationModal(false)}
                            >
                                <div
                                    className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]"
                                    onClick={e => e.stopPropagation()}
                                >
                                    <div className="flex items-center justify-between gap-3 px-5 py-4 bg-gradient-to-r from-red-50 to-white border-b border-red-200">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-xl bg-red-100 text-red-700 flex items-center justify-center">
                                                <ShieldAlert size={18}/>
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-red-900">Tutte le escalation · ultimi 7 giorni</p>
                                                <p className="text-[11px] text-red-700/80 font-medium">
                                                    {escalations.length} notifiche totali
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setShowEscalationModal(false)}
                                            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                                        >
                                            <XCircle className="w-5 h-5"/>
                                        </button>
                                    </div>
                                    <div className="divide-y divide-gray-100 flex-1 overflow-y-auto">
                                        {pageRows.map(renderRow)}
                                    </div>
                                    {totalPages > 1 && (
                                        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-100 bg-gray-50">
                                            <span className="text-[11px] text-gray-600 font-medium">
                                                Pagina {currentPage} di {totalPages}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => setEscalationPage(p => Math.max(1, p - 1))}
                                                    disabled={currentPage <= 1}
                                                    className="text-[11px] font-bold text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg transition-colors"
                                                >
                                                    Precedente
                                                </button>
                                                <button
                                                    onClick={() => setEscalationPage(p => Math.min(totalPages, p + 1))}
                                                    disabled={currentPage >= totalPages}
                                                    className="text-[11px] font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg transition-colors"
                                                >
                                                    Successiva
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                );
            })()}

            {/*
              Banner "Hai N report da rifare": il click apre il primo incarico, dove il
              ReportTab mostra già il form compilato e il motivo della richiesta.
            */}
            {user?.role === UserRole.COLLABORATORE && relazioniDaModificare.length > 0 && (
                <div
                    onClick={() => {
                        const first = relazioniDaModificare[0];
                        navigate('/incarichi', {
                            state: {
                                openIncaricoId: first?.incaricoId,
                                openDate: first?.dataRelazione,
                                openTab: 'spese',
                            },
                        });
                    }}
                    className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer p-5 flex items-center gap-5"
                >
                    <div className="w-14 h-14 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                        <AlertTriangle size={26}/>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">Report da rifare</p>
                        <p className="text-2xl font-black text-slate-900 leading-tight">
                            {relazioniDaModificare.length} <span className="text-sm font-bold text-slate-500">
                                {relazioniDaModificare.length === 1 ? 'report ti è stato rimandato' : 'report ti sono stati rimandati'} dal titolare
                            </span>
                        </p>
                        <p className="text-[11px] font-semibold text-amber-700 mt-1">Clicca per aprire e correggere</p>
                    </div>
                </div>
            )}

            {/* Widget "Da Approvare" (admin/responsabile), apre l'hub Approvazioni. */}
            {/*
              Visibile solo se ci sono media o lavori da approvare. Le spese non sono
              contate qui ma si vedono comunque in Approvazioni.
            */}
            {(() => {
                if (user?.role === UserRole.COLLABORATORE) return null;
                const totaleVisibile = (approvazioniSummary.media || 0) + (approvazioniSummary.lavori || 0);
                if (totaleVisibile <= 0) return null;
                return (
                    <div
                        onClick={() => navigate('/approvazioni')}
                        className="bg-gradient-to-r from-rose-50 to-amber-50 border border-rose-200 rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer p-5 flex items-center gap-5"
                    >
                        <div className="w-14 h-14 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                            <CheckSquare size={26}/>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-rose-700 uppercase tracking-wider">Da Approvare</p>
                            <p className="text-2xl font-black text-slate-900 leading-tight">
                                {totaleVisibile} <span className="text-sm font-bold text-slate-500">elementi in attesa</span>
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-[11px] font-bold text-slate-600 flex-wrap">
                                <span className="flex items-center gap-1.5">
                                    <ShieldAlert size={12} className="text-blue-500"/>
                                    {approvazioniSummary.media} media
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <Receipt size={12} className="text-indigo-500"/>
                                    {approvazioniSummary.lavori} lavori
                                </span>
                            </div>
                        </div>
                        <div className="hidden sm:block text-rose-600 text-sm font-bold shrink-0">
                            Apri →
                        </div>
                    </div>
                );
            })()}

            {/*
              Incarichi del giorno con orari difformi (pianificato vs reale oltre soglia).
              Solo segnalazione, non blocca nulla.
            */}
            {lavoriDifformiOggi.length > 0 && (
                <div className="bg-gradient-to-r from-rose-50 to-amber-50 border border-rose-200 rounded-2xl shadow-sm p-5">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-11 h-11 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                            <AlertTriangle size={22}/>
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs font-bold text-rose-700 uppercase tracking-wider">Allerta orari difformi</p>
                            <p className="text-lg font-black text-slate-900 leading-tight">
                                {lavoriDifformiOggi.length} <span className="text-xs font-bold text-slate-500">{lavoriDifformiOggi.length === 1 ? 'incarico oggi con scostamento' : 'incarichi oggi con scostamento'} oltre {formatMinutiBreve(sogliaOrariMin)}</span>
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {lavoriDifformiOggi.map(lavoro => {
                            const diff = getOrariDifformiMinuti(lavoro);
                            const titolo = lavoro.titolo
                                || (lavoro.pratica && (lavoro.pratica.nome || lavoro.pratica.name))
                                || `Incarico #${lavoro.id}`;
                            const collab = lavoro.collaboratore
                                ? `${lavoro.collaboratore.name || ''} ${lavoro.collaboratore.surname || ''}`.trim()
                                : null;
                            return (
                                <div
                                    key={lavoro.id}
                                    onClick={() => {
                                        if (user?.role === UserRole.COLLABORATORE) {
                                            // Stesso deep-link del banner "report da rifare".
                                            navigate('/incarichi', { state: { openIncaricoId: lavoro.id } });
                                        } else {
                                            navigate(`/pratiche/${lavoro.praticaId || lavoro.pratica?.id}`);
                                        }
                                    }}
                                    className="bg-white border border-rose-200 rounded-lg p-2.5 cursor-pointer hover:shadow-md transition-all"
                                >
                                    <div className="text-xs font-bold text-slate-900 truncate">{titolo}</div>
                                    <div className="flex items-center justify-between mt-1 text-[10px] text-slate-500">
                                        <span className="truncate">{collab || '—'}</span>
                                        <span className="font-black text-rose-600 shrink-0 ml-2">+{formatMinutiBreve(diff)}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Lavori di oggi */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 border-l-4 border-l-indigo-500">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <CalendarCheck className="w-6 h-6 text-indigo-600"/>
                        {user?.role === UserRole.COLLABORATORE ? "I Miei Incarichi di Oggi" : "Servizi Investigativi Oggi"}
                    </h3>
                    {user?.role === UserRole.COLLABORATORE && (
                        <button onClick={() => navigate('/incarichi')} className="text-xs text-indigo-600 font-bold hover:underline bg-indigo-50 px-3 py-1.5 rounded-lg">
                            Gestisci Incarichi
                        </button>
                    )}
                </div>

                {lavoriOggi.length === 0 ? (
                    <div className="bg-gray-50 p-6 rounded-xl border border-dashed border-gray-200 text-center text-gray-500 text-sm">
                        Nessun servizio operativo programmato per la giornata di oggi.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {lavoriOggi.map(lavoro => {
                            let collaboratoriText = 'Sconosciuto';
                            if (lavoro.collaboratoriNomi && lavoro.collaboratoriNomi.length > 0) {
                                collaboratoriText = lavoro.collaboratoriNomi.join(', ');
                            } else if (lavoro.collaboratore) {
                                collaboratoriText = `${lavoro.collaboratore.name} ${lavoro.collaboratore.surname}`.trim();
                            }

                            // Orario di lavoro
                            const startH = new Date(lavoro.dataInizio).toLocaleTimeString('it-IT', {hour: '2-digit', minute:'2-digit'});
                            const endH = lavoro.dataFine ? new Date(lavoro.dataFine).toLocaleTimeString('it-IT', {hour: '2-digit', minute:'2-digit'}) : '';
                            // Stato della card, lo stesso che vede il collaboratore (getStatoOperativoIncarico):
                            // - Servizio Espletato: report consegnato
                            // - Report: lavoro fatto, report da consegnare
                            // - In Servizio: Start senza Stop
                            // - Da Fare: tutto il resto
                            const op = getStatoOperativoIncarico(lavoro);
                            const concluso = lavoro.stato === 'COMPLETATO' || isIncaricoConcluso(lavoro) || op?.kind === 'espletato';
                            const daReport = !concluso && op?.kind === 'report';
                            const inServizio = !concluso && !daReport && (op?.kind === 'servizio' || !!lavoro.activeSessionId);
                            const daFare = !concluso && !daReport && !inServizio;
                            // Giornata non di oggi rimasta aperta: data accanto all'orario.
                            const dataLavoro = !isDiOggi(lavoro) && lavoro.dataInizio
                                ? new Date(lavoro.dataInizio).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) + ' · '
                                : '';

                            // Al collaboratore mostriamo "numero mandato - soggetto" senza il cliente,
                            // come in IncaricoCard. Admin e responsabile vedono titolo o nome pratica.
                            const isCollab = user?.role === UserRole.COLLABORATORE;
                            let displayTitle: string;
                            if (isCollab) {
                                const praticaName = (lavoro.pratica as any)?.name || (lavoro.pratica as any)?.nome || '';
                                const numMandato = praticaName.includes(' - ') ? praticaName.split(' - ')[0] : praticaName;
                                let soggetto = lavoro.soggettoNome || '';
                                if (!soggetto) {
                                    try {
                                        const raw = (lavoro.pratica as any)?.datiAggiuntiviJson;
                                        const dati = typeof raw === 'string' ? JSON.parse(raw) : raw || {};
                                        soggetto = dati.soggetti?.[0]?.nome || dati.soggetto_nome || dati.soggetto_denominazione || '';
                                    } catch { /* Dati assenti: resta solo il numero mandato */ }
                                }
                                displayTitle = soggetto ? `${numMandato} - ${soggetto}` : (numMandato || `Incarico #${lavoro.id}`);
                            } else {
                                displayTitle = lavoro.titolo || (lavoro.pratica && (lavoro.pratica.nome || lavoro.pratica.name)) || 'Indagine in corso';
                            }
                            // Collaboratore: deep-link all'incarico in MieiIncarichiView via location.state.
                            // Admin/responsabile: dettaglio del mandato.
                            const onCardClick = () => {
                                if (isCollab) {
                                    navigate('/incarichi', { state: { openIncaricoId: lavoro.id } });
                                } else {
                                    navigate(`/pratiche/${lavoro.praticaId || lavoro.pratica?.id}`);
                                }
                            };

                            // Riferimento del mandato (es. "Milano / Esselunga"): se c'è, prende il posto del luogo.
                            const rifMandato = String((lavoro.pratica as any)?.riferimento ?? '').trim();

                            return (
                                <div key={lavoro.id} className="p-4 bg-indigo-50 border border-indigo-200 rounded-xl hover:shadow-md transition-all cursor-pointer group" onClick={onCardClick}>
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="min-w-0 pr-2">
                                            <div className="text-sm font-extrabold text-indigo-900 truncate group-hover:text-indigo-700 transition-colors">
                                                {displayTitle}
                                            </div>
                                            {rifMandato && (
                                                <span className="mt-1 inline-flex items-center gap-1 max-w-full bg-violet-50 border border-violet-200 text-violet-700 px-2 py-0.5 rounded-md text-xs font-black">
                                                    <Hash size={12} className="shrink-0"/><span className="truncate">RIF. {rifMandato}</span>
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-col items-end gap-1 shrink-0">
                                            <div className="text-[10px] font-bold px-2 py-1 bg-white text-indigo-700 rounded border border-indigo-200 shadow-sm whitespace-nowrap flex items-center gap-1">
                                                <Clock size={10}/>
                                                {dataLavoro}{startH} {endH ? ` - ${endH}` : ''}
                                            </div>
                                            {concluso && (
                                                <span className="text-[10px] font-black uppercase tracking-wider text-white bg-emerald-600 px-2 py-0.5 rounded-full">
                                                    Servizio Espletato
                                                </span>
                                            )}
                                            {daReport && (
                                                <span className="text-[10px] font-black uppercase tracking-wider text-orange-700 bg-orange-100 border border-orange-200 px-2 py-0.5 rounded-full animate-pulse">
                                                    Report
                                                </span>
                                            )}
                                            {inServizio && (
                                                <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                                                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"/>
                                                    In Servizio
                                                </span>
                                            )}
                                            {daFare && (
                                                <span className="text-[10px] font-black uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                                                    Da Fare
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="space-y-1.5 text-[11px] text-indigo-800/80 font-medium">
                                        {!rifMandato && (
                                            <div className="flex items-center gap-2">
                                                <MapPin size={14} className="text-indigo-400 shrink-0"/>
                                                <span className="truncate">{lavoro.luogo || 'Luogo N/D'}</span>
                                            </div>
                                        )}
                                        {/*
                                          Collaboratori assegnati solo per admin/responsabile: la lista /me non
                                          contiene questo campo.
                                        */}
                                        {!isCollab && (
                                            <div className="flex items-center gap-2">
                                                <Users size={14} className="text-indigo-400 shrink-0"/>
                                                <span className="truncate">{collaboratoriText}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/*
              Servizi fiduciari di oggi: tutti i turni per admin/responsabile, solo i
              propri per il collaboratore.
            */}
            {(() => {
                const isCollab = user?.role === UserRole.COLLABORATORE;
                // /servizi/turni/oggi è già filtrato dal BE per il collaboratore.
                const turniVisibili = turniOggi;
                const inServizioCount = turniVisibili.filter((t: any) => t.effettivoInizio && !t.effettivoFine).length;
                // Se il collaboratore non ha turni oggi la sezione non compare.
                if (isCollab && turniVisibili.length === 0) return null;
                return (
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 border-l-4 border-l-emerald-500">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <Shield className="w-6 h-6 text-emerald-600"/>
                            {isCollab ? 'I Miei Servizi Fiduciari Oggi' : 'Servizi Fiduciari Oggi'}
                            {inServizioCount > 0 && (
                                <span className="ml-1 flex items-center gap-1 text-[11px] font-black text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"/>
                                    {inServizioCount} in servizio
                                </span>
                            )}
                        </h3>
                    </div>

                    {turniVisibili.length === 0 ? (
                        <div className="bg-gray-50 p-6 rounded-xl border border-dashed border-gray-200 text-center text-gray-500 text-sm">
                            Nessun turno fiduciario programmato per oggi.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {turniVisibili.map((turno: any) => {
                                const inServizio = !!turno.effettivoInizio && !turno.effettivoFine;
                                const completato = !!turno.effettivoFine;
                                // Turni sempre in verde come il resto dei servizi fiduciari; lo stato è nel badge.
                                const cardBg = 'bg-emerald-50 border-emerald-200';
                                const nomeOp = turno.collaboratore
                                    ? `${turno.collaboratore.name} ${turno.collaboratore.surname}`.trim()
                                    : 'Operatore N/D';
                                const oraPian = `${turno.oraInizio || '?'} - ${turno.oraFine || '?'}`;
                                const oraInizio = turno.effettivoInizio
                                    ? new Date(turno.effettivoInizio).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
                                    : null;
                                const oraFine = turno.effettivoFine
                                    ? new Date(turno.effettivoFine).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
                                    : null;

                                // Card cliccabile: il collaboratore apre il turno via deep-link in /incarichi
                                // (gestito da openTurnoId in MieiIncarichiView), admin e responsabile la
                                // GestioneServizioView con tutti i turni del servizio.
                                const handleCardClick = () => {
                                    if (isCollab) {
                                        navigate('/incarichi', { state: { openTurnoId: turno.id } });
                                    } else if (turno.servizio?.id) {
                                        navigate(`/servizi/${turno.servizio.id}`);
                                    }
                                };
                                return (
                                    <button
                                        type="button"
                                        key={turno.id}
                                        onClick={handleCardClick}
                                        className={`text-left p-4 border rounded-xl transition-all hover:shadow-md hover:-translate-y-0.5 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-400 ${cardBg}`}
                                        title={isCollab ? 'Apri dashboard turno' : 'Apri gestione servizio'}
                                    >
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="min-w-0 pr-2">
                                                <div className="text-sm font-extrabold text-emerald-900 truncate">{turno.servizio?.nome || 'Servizio'}</div>
                                                {turno.servizio?.clientName && (
                                                    <div className="text-[10px] text-gray-500 font-medium truncate">{turno.servizio.clientName}</div>
                                                )}
                                            </div>
                                            <div className={`shrink-0 text-[10px] font-black px-2 py-1 rounded-full flex items-center gap-1 ${
                                                inServizio ? 'bg-emerald-500 text-white'
                                                : completato ? 'bg-blue-100 text-blue-700'
                                                : 'bg-gray-200 text-gray-600'
                                            }`}>
                                                {inServizio && <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"/>}
                                                {inServizio ? 'IN SERVIZIO' : completato ? 'COMPLETATO' : 'DA FARE'}
                                            </div>
                                        </div>
                                        <div className="space-y-1.5 text-[11px] font-medium text-gray-600">
                                            <div className="flex items-center gap-2">
                                                <UserIcon size={12} className="shrink-0 text-gray-400"/>
                                                <span className="truncate">{nomeOp}</span>
                                            </div>
                                            {turno.servizio?.indirizzo && (
                                                <div className="flex items-center gap-2">
                                                    <MapPin size={12} className="shrink-0 text-gray-400"/>
                                                    <span className="truncate">{turno.servizio.indirizzo}</span>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-2">
                                                <Clock size={12} className="shrink-0 text-gray-400"/>
                                                <span>Pianificato: {oraPian}</span>
                                            </div>
                                            {oraInizio && (
                                                <div className={`flex items-center gap-2 font-black ${inServizio ? 'text-emerald-700' : 'text-blue-700'}`}>
                                                    <Activity size={12} className="shrink-0"/>
                                                    <span>
                                                        Entrato {oraInizio}
                                                        {oraFine && ` · Uscito ${oraFine}`}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
                );
            })()}

            {/* Contenuto a due colonne, non mostrato al collaboratore. */}
            {user?.role !== UserRole.COLLABORATORE && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/*
                  Sinistra: servizi investigativi e fiduciari non saldati (admin/responsabile), ordinati per
                  residuo decrescente. `_tipo` sceglie icona e rotta.
                */}
                <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col">
                    <div className="flex items-center justify-between mb-5">
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <div className="bg-orange-100 p-2 rounded-lg text-orange-600"><DollarSign className="w-5 h-5"/></div>
                            Servizi Non Saldati
                        </h3>
                        <button onClick={() => navigate('/amministrazione')} className="text-sm text-orange-600 hover:underline font-medium">Vedi tutte</button>
                    </div>
                    {nonSaldatiAll.length === 0 ? (
                        <div className="text-center text-gray-400 py-10 flex-1 flex items-center justify-center bg-gray-50 rounded-xl border border-dashed border-gray-200 text-sm">
                            Nessun servizio con saldo aperto.
                        </div>
                    ) : (
                        <ul className="space-y-3">
                            {[...nonSaldatiAll]
                                .sort((a, b) => b.importoResiduo - a.importoResiduo)
                                .slice(0, 7)
                                .map(p => {
                                    const percIncassata = p.importoTotale > 0 ? Math.round((p.importoIncassato / p.importoTotale) * 100) : 0;
                                    const isServizio = p._tipo === 'SERVIZIO';
                                    const rotta = isServizio ? `/servizi/${p.id}` : `/pratiche/${p.id}`;
                                    // Investigativi (investigazione, bonifica, consulenza) in indigo, sicurezza in verde.
                                    const etichettaTipo = isServizio ? 'Sicurezza' : (p.tipoServizio || 'INVESTIGAZIONE').toLowerCase();
                                    return (
                                        <li key={`${p._tipo}-${p.id}`} onClick={() => navigate(rotta)} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-white hover:shadow-md transition-all cursor-pointer border border-transparent hover:border-orange-100 group gap-4">
                                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                                <div className={`h-10 w-10 rounded-xl flex items-center justify-center font-bold text-base shrink-0 border transition-colors ${isServizio ? 'bg-emerald-50 text-emerald-600 border-emerald-100 group-hover:bg-emerald-500 group-hover:text-white' : 'bg-indigo-50 text-indigo-600 border-indigo-100 group-hover:bg-indigo-500 group-hover:text-white'}`}>
                                                    {(p.nome || p.rif || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-1.5 mb-0.5">
                                                        <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${isServizio ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'}`}>{etichettaTipo}</span>
                                                    </div>
                                                    <div className="font-bold text-gray-900 text-sm truncate leading-tight">{p.nome || p.rif}</div>
                                                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                                                        <UserIcon size={11} className="text-gray-400 shrink-0"/>
                                                        <span className="truncate">{p.clienteNome}</span>
                                                    </div>
                                                    {/* Barra progresso incasso */}
                                                    <div className="mt-1.5 flex items-center gap-2">
                                                        <div className="flex-1 bg-gray-200 rounded-full h-1.5 max-w-[120px]">
                                                            <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${percIncassata}%` }}/>
                                                        </div>
                                                        <span className="text-[10px] text-gray-400 font-medium">{percIncassata}% incassato</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="text-base font-black text-orange-600">{formatCurrency(p.importoResiduo)}</div>
                                                <div className="text-[10px] text-gray-400 font-medium">su {formatCurrency(p.importoTotale)}</div>
                                            </div>
                                        </li>
                                    );
                                })
                            }
                        </ul>
                    )}
                </div>

                {/* Destra: scadenze e alert */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 h-fit flex flex-col">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <div className="bg-red-100 p-2 rounded-lg text-red-600"><AlertTriangle className="w-5 h-5"/></div>
                            Note & Promemoria
                        </h3>
                        <button onClick={() => setIsScadenzaModalOpen(true)} className="p-2 hover:bg-gray-100 rounded-lg text-blue-600 transition-colors" title="Aggiungi Nota">
                            <Plus size={20}/>
                        </button>
                    </div>

                    <div className="space-y-3 flex-1 overflow-y-auto max-h-[400px] pr-1 scrollbar-hide">
                        {scadenze.length === 0 ? (
                            <div className="text-center py-12 text-gray-400 text-sm bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                <CheckCircle size={32} className="mx-auto mb-2 text-gray-300"/>
                                Tutto in ordine! <br/> Nessun promemoria imminente.
                            </div>
                        ) : (
                            scadenze.map(scad => {
                                const expired = isExpired(scad.dataScadenza);
                                return (
                                    <div key={scad.id} className={`p-4 border rounded-xl transition-all group relative ${expired ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200 hover:border-blue-300 shadow-sm'}`}>
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1.5">
                                                    {scad.dataScadenza && (
                                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded shadow-sm border flex items-center gap-1 ${expired ? 'bg-red-100 text-red-800 border-red-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
                                                            <Clock size={10}/> {formatDate(scad.dataScadenza)}
                                                        </span>
                                                    )}
                                                </div>
                                                <h4 className={`font-bold text-sm ${expired ? 'text-red-900' : 'text-gray-900'}`}>{scad.titolo}</h4>
                                                {scad.note && <p className="text-xs text-gray-600 mt-1 line-clamp-2 leading-relaxed">{scad.note}</p>}
                                                {/*
                                                  "Da: ..." quando la nota è di un altro utente. Il BE valorizza userId solo
                                                  per i ruoli che vedono le note altrui.
                                                */}
                                                {scad.userId && user?.id && scad.userId !== user.id && scad.userFullName && (
                                                    <p className="text-[10px] text-gray-400 mt-1.5 flex items-center gap-1">
                                                        <UserIcon size={10}/>
                                                        Da: <span className="font-semibold text-gray-600">{scad.userFullName}</span>
                                                        {scad.sedeNome && <span className="text-gray-400">· {scad.sedeNome}</span>}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="flex flex-col gap-2 ml-2 opacity-0 group-hover:opacity-100 transition-opacity absolute top-2 right-2 bg-white/90 p-1.5 rounded-lg shadow-sm border border-gray-200">
                                                <button onClick={() => handleCompleteScadenza(scad.id)} className="text-green-600 hover:bg-green-50 p-1 rounded" title="Completa"><CheckCircle size={16}/></button>
                                                <button onClick={() => handleDeleteScadenza(scad.id)} className="text-red-500 hover:bg-red-50 p-1 rounded" title="Elimina"><Trash2 size={16}/></button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
            )}

            {/* Modal Scadenze */}
            {isScadenzaModalOpen && (
                <Modal title="Aggiungi Scadenza o Promemoria" onClose={() => setIsScadenzaModalOpen(false)}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-bold mb-1 text-gray-700">Titolo *</label>
                            <input
                                type="text"
                                placeholder="Cosa devi ricordare?"
                                className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                                value={newScadenza.titolo}
                                onChange={e => setNewScadenza({...newScadenza, titolo: e.target.value})}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold mb-1 text-gray-700">Note (Opzionale)</label>
                            <textarea
                                className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                                rows={3}
                                placeholder="Dettagli aggiuntivi..."
                                value={newScadenza.note}
                                onChange={e => setNewScadenza({...newScadenza, note: e.target.value})}
                            ></textarea>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-bold mb-1 text-gray-700">Data (Opzionale)</label>
                                <input
                                    type="date"
                                    className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-medium text-sm"
                                    value={newScadenza.data}
                                    onChange={e => setNewScadenza({...newScadenza, data: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-1 text-gray-700">Ora (Opzionale)</label>
                                <input
                                    type="time"
                                    className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-medium text-sm"
                                    value={newScadenza.ora}
                                    onChange={e => setNewScadenza({...newScadenza, ora: e.target.value})}
                                />
                            </div>
                        </div>

                        <div className="pt-4 border-t border-gray-100 mt-6">
                            <button
                                onClick={handleAddScadenza}
                                className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-bold hover:bg-blue-700 shadow-sm transition-all active:scale-[0.98]"
                            >
                                Salva Promemoria
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modale "Vedi tutti" dei rifiuti degli ultimi 7 giorni (incarichi e turni). */}
            {showRifiutiModal && (
                <Modal title="Rifiuti degli ultimi 7 giorni" onClose={() => setShowRifiutiModal(false)}>
                    <div className="space-y-3">
                        <p className="text-sm text-gray-500">
                            Elenco completo degli incarichi e dei turni rifiutati dai collaboratori
                            (o auto-rifiutati dal sistema per scadenza) negli ultimi 7 giorni.
                        </p>
                        {rifiutiRecenti.length === 0 ? (
                            <div className="text-center text-gray-400 py-8">Nessun rifiuto registrato.</div>
                        ) : (
                            <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-xl">
                                {rifiutiRecenti.map(r => (
                                    <div
                                        key={`m-${r.tipo}-${r.entityId}`}
                                        onClick={() => {
                                            setShowRifiutiModal(false);
                                            if (r.tipo === 'INCARICO' && r.mandatoId) navigate(`/pratiche/${r.mandatoId}`);
                                            else if (r.tipo === 'TURNO' && r.servizioId) navigate(`/servizi/${r.servizioId}`);
                                        }}
                                        className="px-4 py-3 hover:bg-rose-50/40 cursor-pointer transition-colors"
                                    >
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${r.tipo === 'INCARICO' ? 'bg-blue-100 text-blue-700' : 'bg-indigo-100 text-indigo-700'}`}>
                                                {r.tipo === 'INCARICO' ? 'Incarico' : 'Turno'}
                                            </span>
                                            <span className="font-bold text-sm text-gray-900">{r.riferimento}</span>
                                            <span className="text-xs text-gray-500">· {r.collaboratore}</span>
                                            <span className="ml-auto text-[11px] font-semibold text-rose-700">
                                                {r.dataRifiuto ? new Date(r.dataRifiuto).toLocaleString('it-IT', {
                                                    day: '2-digit', month: '2-digit', year: 'numeric',
                                                    hour: '2-digit', minute: '2-digit',
                                                }) : '—'}
                                            </span>
                                        </div>
                                        {r.motivo && (
                                            <p className="text-xs text-gray-600 italic mt-1">"{r.motivo}"</p>
                                        )}
                                        {r.dataPianificata && (
                                            <p className="text-[11px] text-gray-400 mt-0.5">
                                                Pianificato il {new Date(r.dataPianificata).toLocaleString('it-IT', {
                                                    day: '2-digit', month: '2-digit', year: 'numeric',
                                                    hour: '2-digit', minute: '2-digit',
                                                })}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default DashboardView;